import type {
  MailLogCategory,
  MailLogDetail,
  MailLogEntry,
  MailLogFilters,
  MailLogPage,
  MailSuppressionEntry,
  MailSuppressionPage,
  MailSuppressionReason,
} from '@peles/shared';
import { apiClient } from './client';

/**
 * The Mail log (`/mail-log`): every email and text the application sent, what
 * became of it, a resend, and the addresses no longer mailed.
 */

/** Which messages a request may reach: an event's (and its guests'), a guest's, or all (admin). */
export interface MailLogScope {
  eventId?: string;
  guestId?: string;
}

/** Lists travel comma-joined, the way the endpoint reads them. */
function paramsOf(filters: MailLogFilters): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === '') continue;
    if (Array.isArray(value)) {
      if (value.length > 0) params[LIST_PARAM[key] ?? key] = value.join(',');
    } else {
      params[key] = value as string | number;
    }
  }
  return params;
}

const LIST_PARAM: Record<string, string> = {
  channels: 'channel',
  statuses: 'status',
  deliveries: 'delivery',
  categories: 'category',
};

export async function listMailLog(filters: MailLogFilters): Promise<MailLogPage> {
  const { data } = await apiClient.get<MailLogPage>('/mail-log', { params: paramsOf(filters) });
  return data;
}

export async function listMailCategories(filters: MailLogFilters): Promise<MailLogCategory[]> {
  const { data } = await apiClient.get<{ categories: MailLogCategory[] }>('/mail-log/categories', {
    params: paramsOf(filters),
  });
  return data.categories;
}

export async function getMailLogEntry(id: string, scope: MailLogScope): Promise<MailLogDetail> {
  const { data } = await apiClient.get<MailLogDetail>(`/mail-log/${encodeURIComponent(id)}`, {
    params: paramsOf(scope),
  });
  return data;
}

export async function resendMail(id: string, scope: MailLogScope): Promise<MailLogEntry> {
  const { data } = await apiClient.post<MailLogEntry>(
    `/mail-log/${encodeURIComponent(id)}/resend`,
    undefined,
    { params: paramsOf(scope) },
  );
  return data;
}

export async function listMailSuppressions(query: {
  search?: string;
  reasons?: MailSuppressionReason[];
  limit?: number;
  offset?: number;
}): Promise<MailSuppressionPage> {
  const { data } = await apiClient.get<MailSuppressionPage>('/mail-log/suppressions', {
    params: {
      ...(query.search ? { search: query.search } : {}),
      ...(query.reasons?.length ? { reason: query.reasons.join(',') } : {}),
      ...(query.limit ? { limit: query.limit } : {}),
      ...(query.offset ? { offset: query.offset } : {}),
    },
  });
  return data;
}

export async function addMailSuppression(input: {
  address: string;
  detail?: string;
}): Promise<MailSuppressionEntry> {
  const { data } = await apiClient.post<MailSuppressionEntry>('/mail-log/suppressions', input);
  return data;
}

export async function removeMailSuppression(address: string): Promise<void> {
  await apiClient.delete(`/mail-log/suppressions/${encodeURIComponent(address)}`);
}
